/* ====================================================================
   Lunai — shared appearance controller (Samodejno / Svetlo / Temno)

   Separates the customer's PREFERENCE from the RESOLVED visual theme:
     preference  'auto' | 'light' | 'dark'  → html[data-theme-mode]
     resolved    'light' | 'dark'           → html[data-theme]  (CSS keys
                 on this alone — 'auto' never reaches the stylesheets)

   Persists preference in localStorage 'lunai.theme' (legacy values
   'light'/'dark' written by the old binary switch are honoured as
   explicit choices; anything else means Samodejno).

   Markup contract (snippets/lunai-theme-toggle.liquid):
     [data-theme-option]      radio input, value auto|light|dark
     [data-appearance-menu]   <details> popover wrapper (header variant)
     [data-appearance-summary] its <summary> trigger
   All wiring is delegated on document, so Theme Editor section reloads
   never duplicate listeners; shopify:section:load re-stamps state.

   Emits document CustomEvent 'lunai:theme' with detail
   { theme: resolved, mode: preference } whenever appearance changes
   (detail.theme is unchanged from the old controller for compat).
   ==================================================================== */
(function(){
  const STORE_KEY = 'lunai.theme';       /* preference: 'auto'|'light'|'dark' */
  const NIGHT_KEY = 'lunai.themeNight';  /* per-session time verdict: '1'|'0' */
  /* Samodejno night window (LOCAL device clock): when the OS reports Light,
     Lunai still goes dark from NIGHT_START until NIGHT_END. Canonical home
     of these numbers — the inline pre-paint script in layout/theme.liquid
     must remain self-contained and duplicates them; keep both in sync. */
  const NIGHT_START = 20; /* inclusive, 20:00 */
  const NIGHT_END = 7;    /* exclusive, 07:00 */
  const META_COLORS = { light: '#FAF5EC', dark: '#12151C' }; /* --cream / --noc-ground */
  const LS = window.lunaiStrings || {};
  const rootEl = document.documentElement;
  let darkMQ = null;
  try { darkMQ = window.matchMedia('(prefers-color-scheme: dark)'); } catch(e){}

  function normalizeMode(v){
    return (v === 'light' || v === 'dark' || v === 'auto') ? v : 'auto';
  }

  function isNightNow(){
    const h = new Date().getHours();
    return h >= NIGHT_START || h < NIGHT_END;
  }

  /* Time-derived half of Samodejno, frozen per browsing session so the shop
     never flips mid-visit just because the clock crossed 20:00/07:00. A new
     tab/session re-evaluates; `refresh` forces re-evaluation now (used when
     the customer actively switches to Samodejno, or the OS scheme genuinely
     changes — both are deliberate context changes, not clock surprises). */
  function sessionNight(refresh){
    let n = null;
    if (!refresh) { try { n = sessionStorage.getItem(NIGHT_KEY); } catch(e){} }
    if (n !== '1' && n !== '0') {
      n = isNightNow() ? '1' : '0';
      try { sessionStorage.setItem(NIGHT_KEY, n); } catch(e){}
    }
    return n === '1';
  }

  /* Resolver — MUST stay equivalent to the pre-paint script in
     layout/theme.liquid: explicit choice wins; Samodejno = OS dark → dark,
     else night window → dark, else light. */
  function resolve(mode, refreshNight){
    if (mode === 'light' || mode === 'dark') return mode;
    if (darkMQ && darkMQ.matches) return 'dark';
    return sessionNight(refreshNight) ? 'dark' : 'light';
  }

  /* Preference source of truth: the attribute stamped by the pre-paint
     script; fall back to storage for resilience if that script failed. */
  let stored = null;
  try { stored = localStorage.getItem(STORE_KEY); } catch(e){}
  let mode = normalizeMode(rootEl.getAttribute('data-theme-mode') || stored);

  function updateMetaThemeColor(theme){
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', META_COLORS[theme] || META_COLORS.light);
  }

  function syncControls(){
    document.querySelectorAll('[data-theme-option]').forEach(input => {
      input.checked = input.value === mode;
    });
    /* Trigger tooltip names the CURRENT preference ("Videz: Samodejno"). */
    const modeName = LS['general.appearance_' + mode] || '';
    const label = LS['general.appearance'] || '';
    if (label && modeName) {
      document.querySelectorAll('[data-appearance-summary]').forEach(sum => {
        sum.setAttribute('title', label + ': ' + modeName);
      });
    }
  }

  function apply(opts){
    opts = opts || {};
    const theme = resolve(mode, opts.refreshNight);
    rootEl.setAttribute('data-theme', theme);
    rootEl.setAttribute('data-theme-mode', mode);
    updateMetaThemeColor(theme);
    syncControls();
    if (opts.dispatch !== false) {
      /* detail.theme kept for any legacy consumer; detail.mode is new. */
      document.dispatchEvent(new CustomEvent('lunai:theme', { detail: { theme, mode }}));
    }
  }

  function setMode(next){
    next = normalizeMode(next);
    const becameAuto = next === 'auto' && mode !== 'auto';
    mode = next;
    try { localStorage.setItem(STORE_KEY, next); } catch(e){}
    apply({ refreshNight: becameAuto });
  }

  function closeMenus(returnFocus){
    document.querySelectorAll('[data-appearance-menu][open]').forEach(details => {
      details.removeAttribute('open');
      const sum = details.querySelector('[data-appearance-summary]');
      if (sum) {
        sum.setAttribute('aria-expanded', 'false');
        if (returnFocus) sum.focus();
      }
    });
  }

  /* ── Delegated wiring (Theme Editor reload–safe) ─────────────── */
  /* Native radios commit on every arrow-key move (selection follows
     focus), so the popover only closes on POINTER selection — keyboard
     users keep browsing with the menu open (theme previews live) and
     leave via Escape or Tab. */
  let lastPointerDown = 0;
  document.addEventListener('pointerdown', () => { lastPointerDown = Date.now(); }, true);

  document.addEventListener('change', e => {
    const input = e.target && e.target.closest && e.target.closest('[data-theme-option]');
    if (!input) return;
    try { if (navigator.vibrate) navigator.vibrate(8); } catch(_){}  /* tap haptic */
    setMode(input.value);
    const fromPointer = Date.now() - lastPointerDown < 500;
    if (fromPointer && input.closest('[data-appearance-menu]')) closeMenus(true);
  });

  /* <details> 'toggle' does not bubble — capture phase reaches it. */
  document.addEventListener('toggle', e => {
    const details = e.target && e.target.closest && e.target.closest('[data-appearance-menu]');
    if (!details) return;
    const sum = details.querySelector('[data-appearance-summary]');
    if (sum) sum.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  }, true);

  document.addEventListener('click', e => {
    /* Light dismiss: clicking outside any open appearance popover closes it. */
    if (!e.target.closest || !e.target.closest('[data-appearance-menu]')) closeMenus(false);
  });

  document.addEventListener('focusin', e => {
    /* Tabbing past the last option must not leave a forgotten popover
       floating in the sticky header — close it, WITHOUT stealing focus. */
    if (!e.target.closest || !e.target.closest('[data-appearance-menu]')) closeMenus(false);
  });

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    /* Only act while focus is inside an open popover; a page-wide Escape
       must never teleport focus back to the header. */
    const within = e.target && e.target.closest && e.target.closest('[data-appearance-menu][open]');
    if (within) closeMenus(true);
  });

  /* Samodejno follows a GENUINE OS scheme change immediately; the time
     verdict is refreshed too, so OS Dark→Light at 22:00 still resolves
     Dark via the night window (one resolver, no competing behaviours). */
  if (darkMQ) {
    const onOsChange = () => { if (mode === 'auto') apply({ refreshNight: true }); };
    try { darkMQ.addEventListener('change', onOsChange); }
    catch(e){ try { darkMQ.addListener(onOsChange); } catch(_){} }
  }

  /* Another Lunai tab changed the preference — mirror it (keep this
     session's time verdict; only the preference travels across tabs). */
  window.addEventListener('storage', e => {
    if (e.key !== STORE_KEY) return;
    mode = normalizeMode(e.newValue);
    apply();
  });

  /* Theme Editor re-renders the header section with fresh markup. */
  document.addEventListener('shopify:section:load', syncControls);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncControls);
  } else {
    syncControls();
  }

  /* Confirm the pre-paint verdict (and stamp attributes if the inline
     script was blocked) — same resolver, so no visible flip; the initial
     pass never dispatches lunai:theme (nothing changed). */
  apply({ dispatch: false });

  /* Public API — set() keeps accepting 'light'/'dark' from legacy callers
     and now also 'auto'; get() still returns the RESOLVED theme. */
  window.LunaiTheme = {
    set: setMode,
    get: () => rootEl.getAttribute('data-theme') || 'light',
    getMode: () => mode
  };
})();
